import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bold, Italic, Link2, Unlink, Loader2, Save, RotateCcw, Video, UploadCloud, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * OutreachMailEditor — edit the NL version of a drip mail (subject + body
 * with bold/italic/links + placeholder chips). On save the backend
 * auto-translates FR/EN. The brand shell stays untouched around the body.
 */
export const OutreachMailEditor = ({ step, audience = "agency", onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [subject, setSubject] = useState("");
    const [subjectB, setSubjectB] = useState("");
    const [abStats, setAbStats] = useState(null);
    const [spam, setSpam] = useState(null);
    const [checkingSpam, setCheckingSpam] = useState(false);
    const [ctaLabel, setCtaLabel] = useState("");
    const [customized, setCustomized] = useState(false);
    const [placeholders, setPlaceholders] = useState([]);
    const bodyRef = useRef(null);

    // Remap inline text colors that are unreadable on the white mail (legacy
    // dark-theme overrides: white/near-white, transparent, rgba, <font color>).
    const remap = (col, dark = "#18181b", grey = "#3f3f46") => {
        let r, g, b, a = 1;
        const c = col.trim().toLowerCase();
        const named = { white: 1, snow: 1, ivory: 1, ghostwhite: 0.98, whitesmoke: 0.96, floralwhite: 0.98, aliceblue: 0.97, mintcream: 0.99, honeydew: 0.98, azure: 0.98, lightyellow: 0.97, lightcyan: 0.95, beige: 0.93, linen: 0.94, oldlace: 0.96, seashell: 0.96, lavenderblush: 0.95, transparent: 1 };
        let lum = null;
        if (c in named) lum = named[c];
        else if (c.startsWith("#")) {
            let h = c.slice(1);
            if (h.length === 3 || h.length === 4) h = h.split("").map((x) => x + x).join("");
            if (h.length < 6) return null;
            r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16);
            if (h.length >= 8 && parseInt(h.slice(6, 8), 16) < 128) lum = 1;
            else lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        } else if (c.startsWith("rgb") || c.startsWith("hsl")) {
            const nums = c.match(/[\d.]+%?/g);
            if (!nums || nums.length < 3) return null;
            if (nums.length >= 4) { a = nums[3].endsWith("%") ? parseFloat(nums[3]) / 100 : parseFloat(nums[3]); }
            if (a < 0.5) lum = 1;
            else if (c.startsWith("hsl")) lum = parseFloat(nums[2]) / 100;
            else {
                [r, g, b] = nums.slice(0, 3).map((n) => (n.endsWith("%") ? parseFloat(n) * 2.55 : parseFloat(n)));
                lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
            }
        } else return null;
        if (lum === null) return null;
        if (lum >= 0.8) return dark;
        if (lum >= 0.7) return grey;
        return null;
    };
    const COLOR_VAL = "(#[0-9a-fA-F]{3,8}|rgba?\\([^)]*\\)|hsla?\\([^)]*\\)|transparent|white|snow|ivory|ghostwhite|whitesmoke|floralwhite|aliceblue|mintcream|honeydew|azure|lightyellow|lightcyan|beige|linen|oldlace|seashell|lavenderblush)";
    const sanitizeColors = (html) => html
        .replace(new RegExp(`((?:-webkit-|-moz-)?text-fill-color\\s*:\\s*|(?<![-\\w])color\\s*:\\s*)${COLOR_VAL}`, "gi"),
            (full, prefix, col) => { const fixed = remap(col); return fixed ? `${prefix}${fixed}` : full; })
        .replace(new RegExp(`(<font\\b[^>]*\\bcolor\\s*=\\s*["']?)(#[0-9a-fA-F]{3,8}|[a-zA-Z]+)(["']?)`, "gi"),
            (full, pre, col, post) => { const fixed = remap(col); return fixed ? `${pre}${fixed}${post}` : full; })
        .replace(/opacity\s*:\s*(0(?:\.\d+)?)(?![\d])/gi,
            (full, v) => (parseFloat(v) < 0.35 ? "opacity:1" : full));

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/leads/templates?audience=${audience}`);
            const tpl = (data.templates || []).find((t) => t.step === step);
            setPlaceholders(data.placeholders || []);
            if (tpl) {
                setSubject(tpl.subject?.nl || "");
                setSubjectB(tpl.subject_b?.nl || "");
                setCtaLabel(tpl.cta_label?.nl || "");
                setCustomized(!!tpl.customized);
                if (bodyRef.current) bodyRef.current.innerHTML = sanitizeColors(tpl.body_html?.nl || "");
            }
            try {
                const { data: ab } = await api.get("/admin/leads/ab-stats");
                setAbStats(ab.steps?.[String(step)] || null);
            } catch { /* stats zijn optioneel */ }
        } catch (e) {
            toast.error("Kon template niet laden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [step]);

    const exec = (cmd, val = null) => {
        bodyRef.current?.focus();
        document.execCommand(cmd, false, val);
    };

    const insertLink = () => {
        const url = window.prompt("Link-URL (bv. https://... of {register_url}):");
        if (url) exec("createLink", url);
    };

    const insertPlaceholder = (ph) => {
        bodyRef.current?.focus();
        document.execCommand("insertText", false, ph);
    };

    const save = async () => {
        const body_html = sanitizeColors(bodyRef.current?.innerHTML || "");
        if (!subject.trim() || !body_html.trim()) {
            toast.error("Onderwerp en tekst mogen niet leeg zijn");
            return;
        }
        setSaving(true);
        try {
            const payload = { subject: subject.trim(), subject_b: subjectB.trim() || null, body_html };
            if (step === 1 && ctaLabel.trim()) payload.cta_label = ctaLabel.trim();
            const { data } = await api.put(`/admin/leads/templates/${step}?audience=${audience}`, payload);
            setCustomized(true);
            if (data.spam) setSpam({ a: data.spam, b: null });
            toast.success(data.translated
                ? "Opgeslagen — FR, EN & ES automatisch vertaald"
                : "Opgeslagen (vertaling mislukt — NL wordt overal gebruikt)");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSaving(false);
        }
    };

    const resetDefault = async () => {
        setResetting(true);
        try {
            await api.delete(`/admin/leads/templates/${step}?audience=${audience}`);
            toast.success("Standaardtekst hersteld");
            await load();
            onSaved?.();
        } catch (e) {
            toast.error("Herstellen mislukt");
        } finally {
            setResetting(false);
        }
    };

    // All mails render on the WHITE shell — the editor mirrors that so
    // what you see is what the lead gets.

    return (
        <div className="p-5 space-y-4" data-testid="outreach-mail-editor">
            {loading && (
                <div className="flex items-center gap-2 text-zinc-500 text-sm py-8 justify-center">
                    <Loader2 className="h-4 w-4 animate-spin" /> Template laden…
                </div>
            )}
            <div className={loading ? "hidden" : "space-y-4"}>
                <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-zinc-500">
                        Je bewerkt de <strong className="text-zinc-600">Nederlandse</strong> versie — FR, EN &amp; ES worden bij opslaan automatisch vertaald.
                        {customized && <span className="ml-2 text-[#E5A93D]">● aangepast</span>}
                    </p>
                    {customized && (
                        <button
                            onClick={resetDefault}
                            disabled={resetting}
                            className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-950 border border-zinc-200 rounded-sm px-2.5 h-8 hover:bg-zinc-100"
                            data-testid="outreach-reset-default"
                        >
                            {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                            Herstel standaard
                        </button>
                    )}
                </div>

                <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">Onderwerp (A)</label>
                    <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full h-10 bg-[#FFFFFF] border border-zinc-200 rounded-sm px-3 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                        data-testid="outreach-subject-input"
                    />
                </div>

                <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
                        Onderwerp B <span className="text-zinc-400 normal-case tracking-normal font-normal">— optioneel, A/B-test (50/50 afgewisseld)</span>
                    </label>
                    <input
                        value={subjectB}
                        onChange={(e) => setSubjectB(e.target.value)}
                        placeholder="Laat leeg om geen A/B-test te draaien"
                        className="w-full h-10 bg-[#FFFFFF] border border-zinc-200 rounded-sm px-3 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D] placeholder:text-zinc-400"
                        data-testid="outreach-subject-b-input"
                    />
                    {abStats && (abStats.A || abStats.B) && (() => {
                        const a = abStats.A || { sent: 0, clicks: 0 };
                        const b = abStats.B || { sent: 0, clicks: 0 };
                        const rate = (v) => (v.sent ? ((v.clicks / v.sent) * 100).toFixed(1) : "0.0");
                        const winner = (b.sent >= 5 || a.sent >= 5) && parseFloat(rate(a)) !== parseFloat(rate(b))
                            ? (parseFloat(rate(a)) > parseFloat(rate(b)) ? "A" : "B") : null;
                        return (
                            <div className="mt-2 text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 flex flex-wrap gap-x-4 gap-y-1" data-testid="outreach-ab-stats">
                                <span><strong>A:</strong> {a.sent} verzonden · {a.clicks} clicks ({rate(a)}%) {winner === "A" && "🏆"}</span>
                                <span><strong>B:</strong> {b.sent} verzonden · {b.clicks} clicks ({rate(b)}%) {winner === "B" && "🏆"}</span>
                                {!winner && <span className="text-zinc-400">nog te weinig data voor een winnaar</span>}
                            </div>
                        );
                    })()}
                </div>

                {step === 1 && (
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">Knoptekst (CTA)</label>
                        <input
                            value={ctaLabel}
                            onChange={(e) => setCtaLabel(e.target.value)}
                            className="w-full h-10 bg-[#FFFFFF] border border-zinc-200 rounded-sm px-3 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                            data-testid="outreach-cta-input"
                        />
                    </div>
                )}

                <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">Tekst</label>
                    {/* Mini toolbar */}
                    <div className="flex items-center gap-1 mb-1.5">
                        <button onClick={() => exec("bold")} className="h-8 w-8 flex items-center justify-center border border-zinc-200 rounded-sm text-zinc-600 hover:bg-zinc-100" title="Vet" data-testid="outreach-toolbar-bold"><Bold className="h-3.5 w-3.5" /></button>
                        <button onClick={() => exec("italic")} className="h-8 w-8 flex items-center justify-center border border-zinc-200 rounded-sm text-zinc-600 hover:bg-zinc-100" title="Cursief" data-testid="outreach-toolbar-italic"><Italic className="h-3.5 w-3.5" /></button>
                        <button onClick={insertLink} className="h-8 w-8 flex items-center justify-center border border-zinc-200 rounded-sm text-zinc-600 hover:bg-zinc-100" title="Link toevoegen" data-testid="outreach-toolbar-link"><Link2 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => exec("unlink")} className="h-8 w-8 flex items-center justify-center border border-zinc-200 rounded-sm text-zinc-600 hover:bg-zinc-100" title="Link verwijderen" data-testid="outreach-toolbar-unlink"><Unlink className="h-3.5 w-3.5" /></button>
                    </div>
                    <div
                        ref={bodyRef}
                        contentEditable
                        suppressContentEditableWarning
                        onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData("text/plain");
                            document.execCommand("insertText", false, text);
                        }}
                        className="min-h-[320px] max-h-[46vh] overflow-y-auto border rounded-sm p-4 text-[14px] leading-relaxed focus:outline-none focus:border-[#E5A93D] bg-white border-zinc-300 text-[#1a1a1a]"
                        data-testid="outreach-body-editor"
                    />
                    {/* Placeholder chips */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span className="text-[10px] text-zinc-500 mr-1">Invoegen:</span>
                        {placeholders.map((ph) => (
                            <button
                                key={ph}
                                onClick={() => insertPlaceholder(ph)}
                                className="text-[11px] font-mono px-2 h-6 border border-zinc-200 rounded-sm text-zinc-500 hover:text-[#E5A93D] hover:border-[#E5A93D]/40 bg-white"
                                data-testid={`outreach-placeholder-${ph.replace(/[{}]/g, "")}`}
                            >
                                {ph}
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-1.5">
                        {"{aanhef}"} wordt automatisch "Hallo Marc," / "Beste Immo X," per lead. Videolinks wijzen naar de gebrande videopagina's.
                    </p>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="h-10 px-5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm"
                        data-testid="outreach-save-btn"
                    >
                        {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Opslaan &amp; vertalen…</> : <><Save className="h-4 w-4 mr-1.5" /> Opslaan</>}
                    </Button>
                    <button
                        type="button"
                        onClick={async () => {
                            setCheckingSpam(true);
                            try {
                                const { data } = await api.post("/admin/leads/spam-check", {
                                    subject: subject.trim(),
                                    subject_b: subjectB.trim() || null,
                                    body_html: bodyRef.current?.innerHTML || "",
                                });
                                setSpam(data);
                            } catch { toast.error("Spam-check mislukt"); }
                            finally { setCheckingSpam(false); }
                        }}
                        disabled={checkingSpam}
                        className="inline-flex items-center gap-1.5 h-10 px-4 text-xs font-bold border border-zinc-300 rounded-sm text-zinc-700 hover:bg-zinc-100"
                        data-testid="outreach-spam-check-btn"
                    >
                        {checkingSpam ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Spam-check
                    </button>
                </div>
                {spam?.a && (() => {
                    const badge = (r) => r.level === "ok"
                        ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                        : r.level === "warn" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-red-100 text-red-700 border-red-200";
                    const label = (r) => r.level === "ok" ? "Inbox-vriendelijk" : r.level === "warn" ? "Let op" : "Hoog spamrisico";
                    const allTriggers = [
                        ...(spam.a.triggers || []),
                        ...((spam.b?.triggers || []).filter((t) => !(spam.a.triggers || []).includes(t)).map((t) => `[Onderwerp B] ${t}`)),
                    ];
                    const worst = spam.b && spam.b.score > spam.a.score ? spam.b : spam.a;
                    return (
                        <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 space-y-2" data-testid="outreach-spam-result">
                            <div className="flex items-center gap-2">
                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-sm border ${badge(worst)}`}>
                                    {label(worst)} — score {worst.score}/10
                                </span>
                            </div>
                            {allTriggers.length > 0 ? (
                                <ul className="text-[11px] text-zinc-600 space-y-0.5 list-disc pl-4">
                                    {allTriggers.map((t, i) => <li key={i}>{t}</li>)}
                                </ul>
                            ) : (
                                <p className="text-[11px] text-emerald-600">Geen spam-triggers gevonden ✓</p>
                            )}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

/**
 * OutreachVideosEditor — manage the 3 videos behind the branded
 * /video/{slug} pages: title (shown on the page) + video URL.
 */
export const OutreachVideosEditor = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState({}); // slug -> percent
    const fileInputs = useRef({});

    useEffect(() => {
        api.get("/admin/outreach/videos")
            .then((r) => setVideos(r.data.videos || []))
            .catch(() => toast.error("Kon video's niet laden"))
            .finally(() => setLoading(false));
    }, []);

    const update = (i, field, val) => {
        setVideos((vs) => vs.map((v, idx) => (idx === i ? { ...v, [field]: val } : v)));
    };

    // Direct-to-R2 upload: presigned PUT so grote videobestanden nooit door
    // de backend-pod (en de ingress body-limit) moeten.
    const uploadVideo = async (i, file) => {
        const slug = videos[i].slug;
        if (!file.type.startsWith("video/")) {
            toast.error("Kies een videobestand (.mp4, .mov, .m4v)");
            return;
        }
        setUploading((u) => ({ ...u, [slug]: 0 }));
        try {
            const { data: init } = await api.post("/upload/direct/init", {
                filename: file.name,
                content_type: file.type || "video/mp4",
            });
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("PUT", init.upload_url);
                xhr.setRequestHeader("Content-Type", init.content_type);
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) {
                        setUploading((u) => ({ ...u, [slug]: Math.round((e.loaded / e.total) * 100) }));
                    }
                };
                xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload mislukt (${xhr.status})`)));
                xhr.onerror = () => reject(new Error("Netwerkfout tijdens upload"));
                xhr.send(file);
            });
            update(i, "url", init.final_url);
            toast.success(`Video geüpload — klik "Video's opslaan" om te activeren`);
        } catch (e) {
            toast.error(e.message || "Upload mislukt");
        } finally {
            setUploading((u) => { const n = { ...u }; delete n[slug]; return n; });
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/outreach/videos", { videos });
            toast.success("Video's opgeslagen — de mails linken automatisch naar de nieuwe video's");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex items-center gap-2 text-zinc-500 text-sm py-10 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Laden…</div>;
    }

    return (
        <div className="p-5 space-y-5" data-testid="outreach-videos-editor">
            <p className="text-xs text-zinc-500 leading-relaxed">
                Deze video's staan op de gebrande pagina's waar de maillinks naartoe wijzen
                (<span className="font-mono text-zinc-500">/video/video-1</span> t.e.m. <span className="font-mono text-zinc-500">video-3</span>).
                Wissel je hier een video-URL, dan tonen alle verzonden én toekomstige mails automatisch de nieuwe video — de mails zelf hoeven niet aangepast.
            </p>
            {videos.map((v, i) => (
                <div key={v.slug} className="border border-zinc-200 rounded-sm p-4 space-y-3 bg-white" data-testid={`outreach-video-row-${v.slug}`}>
                    <div className="flex items-center gap-2 text-xs text-[#E5A93D] font-bold uppercase tracking-[0.15em]">
                        <Video className="h-3.5 w-3.5" /> {v.slug}
                        {!(v.url || "").trim() && (
                            <span className="normal-case tracking-normal font-normal text-amber-500/90">
                                — nog geen video: upload er één, de pagina blijft tot dan verborgen
                            </span>
                        )}
                        <a href={`/video/${v.slug}`} target="_blank" rel="noreferrer" className="ml-auto text-zinc-500 hover:text-zinc-950 normal-case tracking-normal font-normal underline" data-testid={`outreach-video-open-${v.slug}`}>
                            Bekijk pagina →
                        </a>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1">Titel</label>
                        <input
                            value={v.title}
                            onChange={(e) => update(i, "title", e.target.value)}
                            className="w-full h-9 bg-[#FFFFFF] border border-zinc-200 rounded-sm px-3 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                            data-testid={`outreach-video-title-${v.slug}`}
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1">Video</label>
                        <div className="flex items-center gap-2 mb-2">
                            <input
                                ref={(el) => { fileInputs.current[v.slug] = el; }}
                                type="file"
                                accept="video/mp4,video/quicktime,video/x-m4v,.mp4,.mov,.m4v"
                                className="hidden"
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVideo(i, f); e.target.value = ""; }}
                                data-testid={`outreach-video-file-${v.slug}`}
                            />
                            <button
                                type="button"
                                onClick={() => fileInputs.current[v.slug]?.click()}
                                disabled={uploading[v.slug] !== undefined}
                                className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold border border-[#E5A93D]/40 text-[#E5A93D] rounded-sm hover:bg-[#E5A93D]/10 disabled:opacity-60"
                                data-testid={`outreach-video-upload-${v.slug}`}
                            >
                                {uploading[v.slug] !== undefined
                                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {uploading[v.slug]}%</>
                                    : <><UploadCloud className="h-3.5 w-3.5" /> Nieuwe video uploaden</>}
                            </button>
                            <span className="text-[10px] text-zinc-400">of plak hieronder een URL</span>
                        </div>
                        {uploading[v.slug] !== undefined && (
                            <div className="h-1 bg-zinc-100 rounded-full overflow-hidden mb-2">
                                <div className="h-full bg-[#E5A93D] transition-[width] duration-200" style={{ width: `${uploading[v.slug]}%` }} />
                            </div>
                        )}
                        <input
                            value={v.url}
                            onChange={(e) => update(i, "url", e.target.value)}
                            className="w-full h-9 bg-[#FFFFFF] border border-zinc-200 rounded-sm px-3 text-xs font-mono text-zinc-600 focus:outline-none focus:border-[#E5A93D]"
                            data-testid={`outreach-video-url-${v.slug}`}
                        />
                    </div>
                </div>
            ))}
            <Button onClick={save} disabled={saving} className="h-10 px-5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm" data-testid="outreach-videos-save-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Opslaan…</> : <><Save className="h-4 w-4 mr-1.5" /> Video's opslaan</>}
            </Button>
        </div>
    );
};
